import {
  DEFAULT_LOCALE,
  localeChain,
  normalizeLocale,
  userLocaleOrder,
} from '@cloud/shared/i18n/locale'
import { useLocaleSelector } from 'gt-react'
import { useEffect, useRef } from 'react'
import { client } from '@/lib/orpcClient'
import { useUser } from '@/providers/user-provider'

export const LOCALE_STORAGE_KEY = 'gt:locale'

export type UserLanguage = { own: string | null; employee: string | null; tenant: string | null }

/**
 * De onthouden keuze om toe te passen, of `null`. Een keuze van vóór de taal-regio-codes
 * (`nl`) wordt `nl-NL` in plaats van genegeerd — anders viel iedereen die ooit een taal koos
 * na de update stil terug op de browsertaal.
 */
export function storedLocaleToApply(
  stored: string | null,
  current: string,
  locales: readonly string[],
): string | null {
  const code = normalizeLocale(stored)
  if (!code || code === current || !locales.includes(code)) return null
  return code
}

/** Een code teruggebracht op een taal die de app heeft: `de-AT` → `de-DE`, `en-US` → `en-GB`. */
function fitLocale(code: string, locales: readonly string[]): string {
  return localeChain(code).find((c) => locales.includes(c)) ?? DEFAULT_LOCALE
}

/**
 * De schermtaal van een ingelogde gebruiker (spec 2026-09-26-taalcodes §3.5): eigen taal →
 * een keuze van vóór de eigen taal die nog in deze browser stond (`adoptStored`: die wordt zijn
 * eigen taal) →
 * medewerker → tenant → browser, teruggebracht op een taal die de app heeft.
 */
export function resolveStartLocale(o: {
  language: UserLanguage
  stored: string | null
  browser: string | null
  locales: readonly string[]
}): { locale: string; adoptStored: boolean } {
  // Alleen een OUDE code (`nl`, `de`) in de browser is een keuze van vóór de eigen taal. Een
  // volledige code heeft de app daar zelf neergezet — afgeleid van tenant of medewerker, of
  // van een vorige gebruiker op dezelfde computer — en die overnemen zou iemand vastzetten op
  // een taal die hij nooit koos.
  const legacyChoice = o.stored && normalizeLocale(o.stored) !== o.stored ? o.stored : null
  const { locale, adoptStored } = userLocaleOrder({
    ...o.language,
    stored: legacyChoice,
    browser: o.browser,
  })
  return { locale: fitLocale(locale, o.locales), adoptStored }
}

function readStored(): string | null {
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStored(code: string): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, code)
  } catch {
    // alleen gemak; de keuze geldt al
  }
}

/**
 * Past de schermtaal toe zodra bekend is wie er is. Hangt aan de gebruiker, niet aan de mount:
 * inloggen, uitloggen en van gebruiker wisselen in dezelfde sessie lossen de taal opnieuw op.
 * Niet ingelogd: de keuze in deze browser. Ingelogd: `resolveStartLocale`.
 */
export function useLocalePersistence(): void {
  const { locale, locales, setLocale } = useLocaleSelector()
  const { current, loading } = useUser()
  const userId = current?.user?.id ?? null
  const language = current?.user?.language ?? null
  const resolvedFor = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    if (loading || resolvedFor.current === userId) return
    resolvedFor.current = userId
    const stored = readStored()

    if (!userId || !language) {
      const apply = storedLocaleToApply(stored, locale, locales)
      if (apply) {
        setLocale(apply)
        writeStored(apply)
      }
      return
    }

    const browser = typeof navigator !== 'undefined' ? navigator.language : null
    const start = resolveStartLocale({ language, stored, browser, locales })
    if (start.locale !== locale) setLocale(start.locale)
    writeStored(start.locale)
    if (start.adoptStored) {
      client.user.setLanguage({ languageCode: start.locale }).catch((err) => {
        console.error('[locale] eigen taal opslaan mislukt', err)
      })
    }
  }, [loading, userId, language, locale, locales, setLocale])
}
